import React, { useEffect, useState } from 'react';
import AdminApi from './AdminApi';

const AdminMenuPage = ({ currentMenu, clickChangeMenu }) => {
    const [menus, setMenus] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState(null); // {type: 'success'|'error', text: string}

    const showSuccess = (text) => setMessage({ type: 'success', text });
    const showError = (text) => setMessage({ type: 'error', text });

    const loadData = () => {
        setIsLoading(true);
        Promise.all([AdminApi.getMenus(), AdminApi.getMenuItems()])
            .then(([menusData, itemsData]) => {
                setMenus(Array.isArray(menusData) ? menusData : []);
                setMenuItems(Array.isArray(itemsData) ? itemsData : []);
            })
            .catch(err => {
                console.error("Error fetching menus:", err);
                showError("Không tải được danh sách menu. Vui lòng thử lại.");
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        loadData();
    }, []);

    const hasMenuCode = (code) =>
        menus.some(m => m.code && String(m.code).toLowerCase() === code);

    // Cập nhật giá trị 1 trường của item trong state (chưa lưu)
    const handleFieldChange = (itemId, field, value) => {
        setMenuItems(prev => prev.map(it => (it.id === itemId ? { ...it, [field]: value } : it)));
    };

    // Lưu item (gửi đầy đủ các trường vì BE PUT thay thế toàn bộ entity)
    const handleSaveItem = (item) => {
        const payload = { ...item, order: parseInt(item.order, 10) || 0 };
        AdminApi.saveMenuItem(payload)
            .then(saved => {
                if (saved && saved.id) {
                    setMenuItems(prev => prev.map(it => (it.id === saved.id ? saved : it)));
                }
                showSuccess(`Đã lưu mục "${payload.name}".`);
            })
            .catch(err => {
                console.error("Error saving menu item:", err);
                showError(`Lưu mục "${payload.name}" thất bại.`);
            });
    };

    // Bật/tắt kích hoạt: lưu ngay lập tức
    const handleToggleActive = (item) => {
        const updated = { ...item, active: !item.active, order: parseInt(item.order, 10) || 0 };
        AdminApi.saveMenuItem(updated)
            .then(saved => {
                const next = (saved && saved.id) ? saved : updated;
                setMenuItems(prev => prev.map(it => (it.id === item.id ? next : it)));
                showSuccess(`Đã ${updated.active ? 'kích hoạt' : 'tắt'} mục "${item.name}".`);
            })
            .catch(err => {
                console.error("Error toggling menu item:", err);
                showError(`Cập nhật kích hoạt cho "${item.name}" thất bại.`);
            });
    };

    const handleDeleteItem = (item) => {
        if (!window.confirm(`Bạn có chắc muốn xóa mục "${item.name}"?`)) return;
        AdminApi.deleteMenuItem(item.id)
            .then(() => {
                setMenuItems(prev => prev.filter(it => it.id !== item.id));
                showSuccess(`Đã xóa mục "${item.name}".`);
            })
            .catch(err => {
                console.error("Error deleting menu item:", err);
                showError(`Xóa mục "${item.name}" thất bại.`);
            });
    };

    // Thêm mục mới vào 1 menu
    const handleAddItem = (menu) => {
        const itemsOfMenu = menuItems.filter(it => it.menu === menu.id);
        const maxOrder = itemsOfMenu.reduce((max, it) => Math.max(max, it.order || 0), 0);
        const newItem = {
            name: 'Mục mới',
            url: '/',
            order: maxOrder + 10,
            level: 0,
            active: true,
            menu: menu.id,
        };
        AdminApi.saveMenuItem(newItem)
            .then(saved => {
                if (saved && saved.id) {
                    setMenuItems(prev => [...prev, saved]);
                    showSuccess(`Đã thêm mục mới vào menu "${menu.name}".`);
                } else {
                    loadData();
                    showSuccess(`Đã thêm mục mới vào menu "${menu.name}".`);
                }
            })
            .catch(err => {
                console.error("Error adding menu item:", err);
                showError(`Thêm mục mới vào menu "${menu.name}" thất bại.`);
            });
    };

    // Tạo menu header/footer nếu chưa có.
    // DB yêu cầu các cột Field_x_Enable không được null nên phải gửi giá trị mặc định.
    const handleCreateMenu = (code, name) => {
        AdminApi.saveMenu({
            name,
            code,
            lang: 'vi',
            maxlevel: 2,
            field1Enable: false,
            field2Enable: false,
            field3Enable: false,
            field4Enable: false,
            field5Enable: 0,
        })
            .then(() => {
                loadData();
                showSuccess(`Đã tạo menu "${name}" (code: ${code}).`);
            })
            .catch(err => {
                console.error("Error creating menu:", err);
                showError(`Tạo menu "${name}" thất bại.`);
            });
    };

    const renderItemsOfMenu = (menu) => {
        const items = menuItems
            .filter(it => it.menu === menu.id)
            .sort((a, b) => (a.order || 0) - (b.order || 0));

        return (
            <table className="table table-hover">
                <thead>
                    <tr>
                        <th>Tên mục</th>
                        <th>URL</th>
                        <th width="90px">Thứ tự</th>
                        <th width="90px">Kích hoạt</th>
                        <th width="160px">Thao tác</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr><td colSpan="5" className="text-center">Chưa có mục nào.</td></tr>
                    ) : (
                        items.map(item => (
                            <tr key={item.id}>
                                <td>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={item.name || ''}
                                        onChange={e => handleFieldChange(item.id, 'name', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={item.url || ''}
                                        onChange={e => handleFieldChange(item.id, 'url', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={item.order != null ? item.order : ''}
                                        onChange={e => handleFieldChange(item.id, 'order', e.target.value)}
                                    />
                                </td>
                                <td className="text-center">
                                    <input
                                        type="checkbox"
                                        checked={!!item.active}
                                        onChange={() => handleToggleActive(item)}
                                    />
                                </td>
                                <td>
                                    <a
                                        className="btn btn-success btn-small"
                                        href="#"
                                        style={{ marginRight: '5px' }}
                                        onClick={e => { e.preventDefault(); handleSaveItem(item); }}
                                    >
                                        Lưu
                                    </a>
                                    <a
                                        className="btn btn-danger btn-small"
                                        href="#"
                                        onClick={e => { e.preventDefault(); handleDeleteItem(item); }}
                                    >
                                        Xóa
                                    </a>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        );
    };

    return (
        <div className="social-box">
            <h3 className="page-title">Quản lý Menu</h3>
            <div className="header">
                <div className="btn-group">
                    {!hasMenuCode('header') && (
                        <a
                            className="btn btn-primary"
                            href="#"
                            style={{ marginRight: '5px' }}
                            onClick={e => { e.preventDefault(); handleCreateMenu('header', 'Header'); }}
                        >
                            <i className="icon-plus" /> Tạo menu Header
                        </a>
                    )}
                    {!hasMenuCode('footer') && (
                        <a
                            className="btn btn-primary"
                            href="#"
                            onClick={e => { e.preventDefault(); handleCreateMenu('footer', 'Footer'); }}
                        >
                            <i className="icon-plus" /> Tạo menu Footer
                        </a>
                    )}
                </div>
            </div>
            <div className="body">
                {message && (
                    <div
                        className={message.type === 'success' ? 'alert alert-success' : 'alert alert-danger'}
                        style={{
                            padding: '10px',
                            marginBottom: '15px',
                            borderRadius: '4px',
                            color: message.type === 'success' ? '#3c763d' : '#a94442',
                            backgroundColor: message.type === 'success' ? '#dff0d8' : '#f2dede',
                            border: `1px solid ${message.type === 'success' ? '#d6e9c6' : '#ebccd1'}`,
                        }}
                    >
                        {message.text}
                    </div>
                )}

                {isLoading ? (
                    <p className="text-center">Đang tải...</p>
                ) : menus.length === 0 ? (
                    <p className="text-center">Chưa có menu nào. Dùng nút "Tạo menu Header/Footer" phía trên để bắt đầu.</p>
                ) : (
                    menus.map(menu => (
                        <div key={menu.id} style={{ marginBottom: '30px' }}>
                            <h4>
                                {menu.name} <code>{menu.code}</code>
                                <a
                                    className="btn btn-primary btn-small"
                                    href="#"
                                    style={{ marginLeft: '10px' }}
                                    onClick={e => { e.preventDefault(); handleAddItem(menu); }}
                                >
                                    <i className="icon-plus" /> Thêm mục
                                </a>
                            </h4>
                            {renderItemsOfMenu(menu)}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default AdminMenuPage;
